import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import moment from "moment";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import {
  AddHearingDateValidation,
  MultipleHearingsSchema,
} from "../../../helpers/validationSchema";
import * as XLSX from "xlsx";
import ResponseModal from "../../pages/AssignNeutralBulkCase/ResponseModal";
import { CheckBoxLabel, CustomCheckbox } from "../FormInputs";

const inputModes = { MANUAL: "manual", SINGLE: "single", EXCEL: "excel" };

const resolutionKindOptions = [
  { label: "Arbitration", value: "arbitration" },
  { label: "Mediation", value: "mediation" },
  { label: "Negotiation", value: "negotiation" },
];

const CHANNEL_OPTIONS = [
  { key: "sms", label: "SMS" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
];

export default function MultipleHearings({
  openHearing,
  setOpenHearing,
  setRefresh,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const modifyHearingData =
    openHearing?.modifyFlag && openHearing?.caseIds?.length
      ? openHearing
      : null;
  const [mode, setMode] = useState(inputModes.MANUAL);
  const [excelCaseIds, setExcelCaseIds] = useState([]);
  const [showResponseModal, setShowResponseModal] = useState({ state: false });
  const [qaModal, setQaModal] = useState({ state: false });
  const [qaAnswers, setQaAnswers] = useState({});

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;
    setFieldValue("fileName", file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length === 0) {
          enqueueSnackbar("Excel file is empty", { variant: "error" });
          return;
        }
        const headers = (data[0] || []).map((h) =>
          (h || "").toString().toLowerCase().trim(),
        );
        const colIdx = headers.findIndex((h) =>
          [
            "caseid",
            "case id",
            "id",
            "case_id",
            "case-id",
            "case id/dispute no",
          ].includes(h),
        );

        if (colIdx === -1) {
          enqueueSnackbar(
            "Column not found. Please use one of: caseId, Case Id, id",
            { variant: "error" },
          );
          return;
        }
        const ids = data
          .slice(1)
          .map((row) => row[colIdx])
          .filter((id) => id !== undefined && id !== null && id !== "")
          .map(Number)
          .filter((id) => !isNaN(id) && id > 0);
        if (ids.length === 0) {
          enqueueSnackbar("No valid case IDs found in the file", {
            variant: "warning",
          });
          return;
        }
        setExcelCaseIds(ids);
        enqueueSnackbar(
          `${ids.length} case ID${ids.length > 1 ? "s" : ""} loaded successfully`,
          { variant: "success" },
        );
      } catch (err) {
        enqueueSnackbar(
          "Failed to read Excel file. Please try a valid .xlsx file.",
          { variant: "error" },
        );
      }
    };
    reader.onerror = () =>
      enqueueSnackbar("Error reading file", { variant: "error" });
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const buildPostData = (values) => {
    const date = moment(new Date(values.startDate)).format("YYYY-MM-DD");
    const time = moment(new Date(values?.startTime)).format("HH:mm:ss");
    const selectedTime = moment(`${date} ${time}`);

    const channelPayload = {
      sms: values.channel.sms,
      email: values.channel.email,
      whatsapp: values.channel.whatsapp,
    };

    if (modifyHearingData) {
      return {
        gEventId: modifyHearingData.gEventId || null,
        caseIds: modifyHearingData.caseIds,
        nextHearingDate: {
          date: values.startDate
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          time: values?.startTime
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          hearingType: values?.hearingType,
          remarks: values?.remarks,
        },
        channel: channelPayload,
        modifyFlag: modifyHearingData.modifyFlag,
      };
    }

    if (mode === inputModes.MANUAL || mode === inputModes.SINGLE) {
      return {
        channel: channelPayload,
        nextHearingDate: {
          date: values.startDate
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          time: values?.startTime
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          hearingType: values?.hearingType,
          remarks: values?.remarks,
        },
        resolutionKind: values.resolutionKind,
        startCaseId:
          mode === inputModes.SINGLE ? values?.caseId : values?.startCaseId,
        endCaseId:
          mode === inputModes.SINGLE ? values?.caseId : values?.endCaseId,
        exceptCaseIds: values?.exceptCaseIds,
      };
    }

    return {
      channel: channelPayload,
      nextHearingDate: {
        date: values.startDate
          ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
          : null,
        time: values?.startTime
          ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
          : null,
        hearingType: values?.hearingType,
        remarks: values?.remarks,
      },
      resolutionKind: values.resolutionKind,
      caseIds: excelCaseIds.map((id) => Number(id)),
    };
  };

  const handleBulkHearingResponse = (res, postData) => {
    if (!res) {
      return false;
    }

    if (res?.qaFlag && res?.questions?.length) {
      setQaAnswers({});
      setQaModal({ state: true, questions: res.questions, postData });
      return true;
    }

    if (res?.success === false) {
      enqueueSnackbar(res?.message || "Action failed", { variant: "error" });
      setOpenHearing(false);
      return false;
    }

    setRefresh(true);

    if (modifyHearingData) {
      enqueueSnackbar(res?.message || "Hearing Date Updated Successfully", {
        variant: "success",
      });
      setOpenHearing(false);
      return true;
    }

    setShowResponseModal({ state: true, metaData: res });
    enqueueSnackbar(res?.message || "Hearing Date Updated Successfully", {
      variant: "success",
    });
    return true;
  };

  async function onFormSubmit(values) {

    let isSuccess = false;
    try {
      setLoader({ state: true, message: "Uploading NextHearing Date" });

      if (mode === inputModes.EXCEL && excelCaseIds.length === 0) {
        enqueueSnackbar("Please upload a valid Excel file with case IDs", {
          variant: "warning",
        });
        setLoader({ state: false });
        return;
      }

      if (mode === inputModes.SINGLE && !values.caseId) {
        enqueueSnackbar("Please enter a Case ID", { variant: "warning" });
        setLoader({ state: false });
        return;
      }

      const postData = buildPostData(values);
      console.log("🔥 Post Data:", postData);
      const res = modifyHearingData
        ? await CaseService.modifyHearingEvent(postData)
        : await CaseService.bulkNextHearingDate(postData);
      console.log("🔥 API Response:", res);
      isSuccess = handleBulkHearingResponse(res, postData);
    } catch (error) {
      console.error("🔥 Form Submit Error:", error);
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
      if (!isSuccess) setOpenHearing(false);
    }
  }

  const handleCloseResponseModal = () => {
    setShowResponseModal({ state: false });
    setOpenHearing(false);
    setMode(inputModes.MANUAL);
    setExcelCaseIds([]);
    setQaModal({ state: false });
    setQaAnswers({});
  };

  const handleClose = () => {
    setOpenHearing(false);
    setMode(inputModes.MANUAL);
    setExcelCaseIds([]);
    setQaModal({ state: false });
    setQaAnswers({});
  };

  const handleQaSubmit = async () => {
    const questions = qaModal?.questions || [];
    const firstNoIndex = questions.findIndex(
      (item) => qaAnswers[item.key]?.toLowerCase() === "no",
    );
    const requiredQuestions =
      firstNoIndex >= 0 ? questions.slice(0, firstNoIndex + 1) : questions;
    const hasMissingAnswer = requiredQuestions.some(
      (item) => !qaAnswers[item.key],
    );

    if (hasMissingAnswer) {
      enqueueSnackbar("Please answer all confirmation questions", {
        variant: "warning",
      });
      return;
    }

    try {
      setLoader({ state: true, message: "Submitting Confirmation..." });
      const answerPayload = requiredQuestions.reduce((acc, item) => {
        acc[item.key] = qaAnswers[item.key];
        return acc;
      }, {});
      const payload = {
        ...qaModal.postData,
        qaAnswers: answerPayload,
      };
      const res = modifyHearingData
        ? await CaseService.modifyHearingEvent(payload)
        : await CaseService.bulkNextHearingDate(payload);
      const isSuccess = handleBulkHearingResponse(res, qaModal.postData);
      if (!res?.qaFlag) {
        setQaModal({ state: false });
        setQaAnswers({});
      }
      if (!isSuccess && res?.success === false) {
        setMode(inputModes.MANUAL);
        setExcelCaseIds([]);
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  const inputs = [
    {
      name: "hearingType",
      label: "Hearing Type",
      type: "text",
      required: true,
    },
    { name: "remarks", label: "Remarks", type: "text", required: true },
  ];
  return (
    <>
      <Modal open={!!openHearing} onClose={handleClose} disableRestoreFocus>
        <Overlay>
          <ModalCard>
            {/* ── Header ── */}
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </IconBadge>
                <ModalTitle>
                  {modifyHearingData?.modifyFlag === "cancel"
                    ? "Cancel Hearing"
                    : "Scheduled Hearing"}
                </ModalTitle>
              </HeaderContent>
              <CloseBtn onClick={handleClose} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* ── Formik ── */}
            <ModalBody>
              <Formik
                initialValues={{
                  startCaseId: "",
                  endCaseId: "",
                  caseId: "",
                  exceptCaseIds: "",
                  startDate: null,
                  startTime: null,
                  hearingType: "",
                  remarks: "",
                  fileName: "",
                  resolutionKind: "arbitration",
                  channel: {
                    sms: true,
                    email: false,
                    whatsapp: false,
                  },
                }}
                validationSchema={() => {
                  if (modifyHearingData) return AddHearingDateValidation;
                  if (mode === inputModes.MANUAL) return MultipleHearingsSchema;
                  return MultipleHearingsSchema?.shape({
                    startCaseId: null,
                    endCaseId: null,
                  });
                }}
                onSubmit={onFormSubmit}
                validateOnBlur
                validateOnChange
                enableReinitialize
              >
                {({
                  values,
                  handleChange,
                  errors,
                  handleSubmit,
                  touched,
                  handleBlur,
                  setFieldValue,
                }) => {

                  return (
                    <StyledForm onSubmit={handleSubmit} className="mis-dashboard">
                    {!modifyHearingData && (
                      <FormSection>
                        <SectionLabel>Input Method</SectionLabel>
                        <ModeToggle>
                          <ModeBtn
                            type="button"
                            active={mode === inputModes.MANUAL}
                            onClick={() => {
                              setMode(inputModes.MANUAL);
                              setExcelCaseIds([]);
                              setFieldValue("caseId", "");
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Enter ID Range
                          </ModeBtn>
                          <ModeBtn
                            type="button"
                            active={mode === inputModes.SINGLE}
                            onClick={() => {
                              setMode(inputModes.SINGLE);
                              setExcelCaseIds([]);
                              setFieldValue("startCaseId", "");
                              setFieldValue("endCaseId", "");
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Single Case
                          </ModeBtn>
                          <ModeBtn
                            type="button"
                            active={mode === inputModes.EXCEL}
                            onClick={() => {
                              setMode(inputModes.EXCEL);
                              setExcelCaseIds([]);
                              setFieldValue("caseId", "");
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            Upload Excel
                          </ModeBtn>
                        </ModeToggle>
                      </FormSection>
                    )}

                    {/* Manual Range */}
                    {!modifyHearingData && mode === inputModes.MANUAL ? (
                      <FormSection>
                        <TwoCol>
                          <FormField
                            white
                            input={{
                              type: "number",
                              name: "startCaseId",
                              label: "From CaseId",
                              required: true,
                            }}
                            {...{
                              values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                            }}
                          />
                          <FormField
                            white
                            input={{
                              type: "number",
                              name: "endCaseId",
                              label: "End CaseId",
                              required: true,
                            }}
                            {...{
                              values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                            }}
                          />
                        </TwoCol>
                        <FormField
                          white
                          input={{
                            type: "text",
                            name: "exceptCaseIds",
                            label: "Except Case IDs (comma separated)",
                            required: false,
                            placeholder: "e.g., 102, 109, 120",
                            helperText:
                              "Optional: exclude specific cases from the selected range",
                          }}
                          {...{
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                          }}
                        />
                      </FormSection>
                    ) : !modifyHearingData && mode === inputModes.SINGLE ? (
                      <FormSection>
                        <FormField
                          white
                          input={{
                            type: "number",
                            name: "caseId",
                            label: "Case ID",
                            required: true,
                          }}
                          {...{
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                          }}
                        />
                      </FormSection>
                    ) : !modifyHearingData ? (
                      <FormSection>
                        <ExcelZone>
                          <label
                            htmlFor="excel-upload-hearing"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <HiddenInput
                              id="excel-upload-hearing"
                              type="file"
                              accept=".xlsx, .xls"
                              onChange={(e) =>
                                handleFileUpload(e, setFieldValue)
                              }
                            />
                            <UploadInner loaded={excelCaseIds.length > 0}>
                              {excelCaseIds.length > 0 ? (
                                <>
                                  <UploadCheck>
                                    <svg
                                      width="22"
                                      height="22"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </UploadCheck>
                                  <UploadPrimary>
                                    {excelCaseIds.length} Case IDs Loaded
                                  </UploadPrimary>
                                  <UploadSub>Click to replace file</UploadSub>
                                </>
                              ) : (
                                <>
                                  <UploadCloud>
                                    <svg
                                      width="26"
                                      height="26"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="16 16 12 12 8 16" />
                                      <line x1="12" y1="12" x2="12" y2="21" />
                                      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                    </svg>
                                  </UploadCloud>
                                  <UploadPrimary>
                                    Drop Excel file or click to browse
                                  </UploadPrimary>
                                  <UploadSub>
                                    Column must be named <code>caseId</code>
                                  </UploadSub>
                                </>
                              )}
                            </UploadInner>
                          </label>
                        </ExcelZone>
                      </FormSection>
                    ) : null}

                    {!modifyHearingData && <Divider />}

                    {/* Date & Time */}
                    <FormSection>
                      <SectionLabel>Hearing Schedule</SectionLabel>
                      <TwoCol>
                        <FormField
                          input={{
                            name: "startDate",
                            label: "Date",
                            type: "date",
                          }}
                          placeholderText="N/A"
                          minDate={new Date()}
                          {...{
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }}
                        />
                        <FormField
                          input={{
                            name: "startTime",
                            label: "Time (IST)",
                            type: "time",
                            required: !!values.startDate,
                          }}
                          placeholderText="N/A"
                          {...{
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }}
                        />
                      </TwoCol>
                    </FormSection>

                    {/* Resolution Kind */}
                    {!modifyHearingData && (
                      <FormSection>
                        {[
                          {
                            type: "select",
                            name: "resolutionKind",
                            label: "Case Type",
                            required: true,
                            menuItems: resolutionKindOptions,
                          },
                        ].map((input, key) => (
                          <FormField
                            key={key}
                            menuItems={input.menuItems}
                            white
                            {...{
                              input,
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                        ))}
                      </FormSection>
                    )}

                    {/* Hearing Type & Remarks */}
                    <FormSection>
                      {inputs.map((input, index) => (
                        <FormField
                          key={index}
                          {...{
                            input,
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            setFieldValue,
                          }}
                        />
                      ))}
                    </FormSection>

                    {/* ── Channel Notification Checkboxes ── */}
                    <FormSection>
                      <SectionLabel>Notify Via</SectionLabel>
                      <ChannelRow>
                        {CHANNEL_OPTIONS.map(({ key, label }) => (
                          <CheckboxItem key={key}>
                            <HiddenCheckboxInput
                              id={`multi-channel-${key}`}
                              type="checkbox"
                              checked={values.channel[key]}
                              onChange={(e) =>
                                setFieldValue(
                                  `channel.${key}`,
                                  e.target.checked,
                                )
                              }
                            />
                            <CustomCheckbox
                              checked={values.channel[key]}
                              onClick={() =>
                                setFieldValue(
                                  `channel.${key}`,
                                  !values.channel[key],
                                )
                              }
                            />
                            <CheckBoxLabel htmlFor={`multi-channel-${key}`}>
                              {label}
                            </CheckBoxLabel>
                          </CheckboxItem>
                        ))}
                      </ChannelRow>
                    </FormSection>

                    {/* Buttons */}
                    <FooterActions>
                      <PrimaryOutlinedCloseCTAButton
                        style={{ flex: 1, height: "40px" }}
                        onClick={handleClose}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      <PrimaryCTAButton
                        style={{ flex: 1, height: "40px" }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        Submit
                      </PrimaryCTAButton>
                    </FooterActions>
                  </StyledForm>
                  );
                }}
              </Formik>
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>

      <ResponseModal
        open={showResponseModal?.state}
        onClose={handleCloseResponseModal}
        responseData={showResponseModal?.metaData}
        title="Hearing Update Summary"
      />

      <Modal
        open={qaModal?.state}
        onClose={() => setQaModal({ state: false })}
        disableRestoreFocus
      >
        <Overlay>
          <QaModalCard>
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </IconBadge>
                <ModalTitle>Confirmation Required</ModalTitle>
              </HeaderContent>
              <CloseBtn
                onClick={() => setQaModal({ state: false })}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>
            <ModalBody>
              {qaModal?.questions?.map((item) => (
                <QuestionBlock key={item.key}>
                  <QuestionText>{item.question}</QuestionText>
                  {item.caseIds?.length > 0 && (
                    <CaseIdList>
                      Case IDs: {item.caseIds.join(", ")}
                    </CaseIdList>
                  )}
                  <AnswerRow>
                    {item.answerArray?.map((answer) => (
                      <AnswerOption key={`${item.key}-${answer}`}>
                        <input
                          id={`${item.key}-${answer}`}
                          type="radio"
                          name={item.key}
                          checked={qaAnswers[item.key] === answer}
                          onChange={() =>
                            setQaAnswers((prev) => ({
                              ...prev,
                              [item.key]: answer,
                            }))
                          }
                        />
                        <label htmlFor={`${item.key}-${answer}`}>
                          {answer}
                        </label>
                      </AnswerOption>
                    ))}
                  </AnswerRow>
                </QuestionBlock>
              ))}
              <FooterActions>
                <PrimaryOutlinedCloseCTAButton
                  style={{ flex: 1, height: "40px" }}
                  onClick={() => setQaModal({ state: false })}
                >
                  Cancel
                </PrimaryOutlinedCloseCTAButton>
                <PrimaryCTAButton
                  style={{ flex: 1, height: "40px" }}
                  onClick={handleQaSubmit}
                >
                  Submit
                </PrimaryCTAButton>
              </FooterActions>
            </ModalBody>
          </QaModalCard>
        </Overlay>
      </Modal>
    </>
  );
}

export const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
`;

export const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 560px;
  background: #fff;
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.16),
    0 4px 16px rgba(0, 0, 0, 0.07);
  outline: none;
  animation: ${fadeIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  max-height: 95vh;

  @media (max-width: 480px) {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
    animation: none;
  }
`;

const QaModalCard = styled(ModalCard)`
  max-width: 640px;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f2f5;
  position: relative;
  flex-shrink: 0;
`;

export const HeaderAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

export const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  background: ${COLORS.BTN_GREEN}18;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.3px;
`;

export const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

export const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px 24px;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

export const StyledForm = styled.form`
  width: 100%;
`;

export const FormSection = styled.div`
  margin-bottom: 16px;
`;

export const SectionLabel = styled.h4`
  margin: 0 0 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
`;

export const ModeToggle = styled.div`
  display: flex;
  background: #f4f6f8;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

export const ModeBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  ${(p) =>
    p.active
      ? css`
          background: #fff;
          color: ${COLORS.BTN_GREEN};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          font-weight: 700;
        `
      : css`
          background: transparent;
          color: #6b7280;
          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
  @media (max-width: 360px) {
    font-size: 12px;
    padding: 9px 6px;
  }
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const ExcelZone = styled.div`
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const UploadInner = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  background: ${(p) => (p.loaded ? `${COLORS.BTN_GREEN}08` : "#fafafa")};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${COLORS.BTN_GREEN}08;
  }
`;

export const UploadCloud = styled.div`
  color: #9ca3af;
  margin-bottom: 2px;
`;

export const UploadCheck = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN}20;
  color: ${COLORS.BTN_GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
`;

export const UploadPrimary = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #374151;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

export const UploadSub = styled.div`
  font-size: 12px;
  color: #9ca3af;
  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11px;
    color: ${COLORS.BTN_GREEN};
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
  margin: 4px 0 18px;
`;

export const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
  @media (max-width: 400px) {
    flex-direction: column-reverse;
  }
`;

export const ChannelRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

export const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

export const HiddenCheckboxInput = styled.input`
  display: none;
`;

export const StyledCheckbox = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 2px solid ${(p) => (p.checked ? COLORS.BTN_GREEN : "#d1d5db")};
  background: ${(p) => (p.checked ? COLORS.BTN_GREEN : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  flex-shrink: 0;
  cursor: pointer;
`;

export const CheckboxLabelText = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const QuestionBlock = styled.div`
  padding: 14px 0;
  border-bottom: 1px solid #f0f2f5;

  &:first-child {
    padding-top: 0;
  }
`;

const QuestionText = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
`;

const CaseIdList = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #6b7280;
  word-break: break-word;
`;

const AnswerRow = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const AnswerOption = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #374151;

  input,
  label {
    cursor: pointer;
  }
`;
